import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { FulfillmentType } from 'shop/types'
import { PartnerStore, StoreRankReasons } from '../types'
import { formatEarliestSlot, getStoreTagContent } from './utils'
import { useCallback, useContext, useMemo, useState } from 'react'
import { format, parseISO } from 'date-fns'
import { LandingContext } from '../LandingContext'
import { disableBlueTapHighlight } from 'shop/components/common'
import { TagItems } from './StoreTags'
import RouterAnchor from 'shop/components/RouterAnchor/RouterAnchor'
import { useShop } from 'shop/hooks'

type FulfillmentInfoProps = {
  store: PartnerStore
  onLoadDateTime: Date
  isDeliveryEnabled: boolean
  isPickupEnabled: boolean
  sortedRankReasons: StoreRankReasons[]
}

const FulfillmentInfo = ({
  store,
  onLoadDateTime,
  isDeliveryEnabled,
  isPickupEnabled,
  sortedRankReasons
}: FulfillmentInfoProps) => {
  if (!isDeliveryEnabled && !isPickupEnabled) return <></>

  return (
    <FulfillmentInfoContainer>
      {isDeliveryEnabled && (
        <FulfillmentLink
          sortedRankReasons={sortedRankReasons}
          fulfillmentType='DELIVERY'
          store={store}
          onLoadDateTime={onLoadDateTime}
        />
      )}
      {isPickupEnabled && (
        <FulfillmentLink
          sortedRankReasons={sortedRankReasons}
          fulfillmentType='PICKUP'
          store={store}
          onLoadDateTime={onLoadDateTime}
        />
      )}
    </FulfillmentInfoContainer>
  )
}

type FulfillmentLinkProps = {
  sortedRankReasons: StoreRankReasons[]
  fulfillmentType: FulfillmentType
  store: PartnerStore
  onLoadDateTime: Date
}

const FulfillmentLink = ({
  sortedRankReasons,
  fulfillmentType,
  store,
  onLoadDateTime
}: FulfillmentLinkProps) => {
  const [navQueryDate, setNavQueryDate] = useState<string | null>(null)
  const [navQueryTime, setNavQueryTime] = useState<string | null>(null)
  const { sortCriteria, currentLocation } = useContext(LandingContext)
  const { fetchPartnerAndCustomer } = useShop()

  const isAcceptingOrders = !!store.orderFulfillmentTypes.filter(
    (oft) =>
      oft.fulfillmentType === fulfillmentType &&
      oft.status === 'ACCEPTING_ORDERS'
  ).length

  const filteredOFT = store.orderFulfillmentTypes.filter(
    (oft) =>
      oft.fulfillmentType === fulfillmentType &&
      oft.status === 'ACCEPTING_ORDERS'
  )
  let earliestSlot: {
    from: string
    to: string
  } | null = null
  if (!!filteredOFT.length) earliestSlot = filteredOFT[0].earliestTimeslot
  const formattedEarliestSlot = formatEarliestSlot(earliestSlot, onLoadDateTime)

  /** Checks for the appropriate rank reasons. Reflects user flexibility preferences */
  const isDateOrTimeAvailable = (): boolean => {
    // no sortCriteria.fulfillmentDate means "Earliest" - return true to handle availability elsewhere.
    if (!sortCriteria.fulfillmentDate) return true
    return !!sortedRankReasons.find(
      (reason) =>
        reason === `${fulfillmentType}_ORDER_DATE_AVAILABLE` ||
        reason === `${fulfillmentType}_ORDER_DATETIME_AVAILABLE`
    )
  }

  // Render different text content depending on if the store is accepting orders or not
  const getLinkContent = useCallback(() => {
    const sortedFulfillmentTypes = sortCriteria.fulfillmentTypes
    const { isEnabled, text } = getStoreTagContent({
      tag: fulfillmentType as TagItems,
      isDeliveryEnabled: fulfillmentType === 'DELIVERY',
      isPickupEnabled: fulfillmentType === 'PICKUP',
      hasLocation: !!currentLocation?.address,
      sortCriteria,
      sortedRankReasons
    })
    const fulfillmentTypeLowerCase = fulfillmentType.toLowerCase()
    if (!sortedFulfillmentTypes.includes(fulfillmentType)) {
      return {
        topContent: `View ${fulfillmentTypeLowerCase} Menu`,
        bottomContent: null,
        isActive: false
      }
    }
    if (!isAcceptingOrders || !isEnabled) {
      return {
        topContent: `View ${fulfillmentTypeLowerCase} Menu`,
        bottomContent: isAcceptingOrders ? text : 'Not accepting orders',
        isActive: isAcceptingOrders && isEnabled
      }
    }
    if (!isDateOrTimeAvailable()) {
      return {
        topContent: `Order ${fulfillmentTypeLowerCase}`,
        bottomContent: formattedEarliestSlot,
        isActive: false
      }
    }
    return {
      topContent: `Order ${fulfillmentTypeLowerCase}`,
      bottomContent: formattedEarliestSlot,
      isActive: isAcceptingOrders || isEnabled
    }
  }, [
    fulfillmentType,
    formattedEarliestSlot,
    isAcceptingOrders,
    currentLocation,
    sortCriteria,
    sortedRankReasons
  ])

  const storeHref = `/order/store/${store.slug}`
  const searchQueryParams = useMemo(() => {
    let queryParams = []
    if (fulfillmentType)
      queryParams.push(`fulfillment_type=${fulfillmentType.toLowerCase()}`)

    if (sortCriteria.fulfillmentDate || sortCriteria.fulfillmentTime) {
      const matchedStoreOFT = store.orderFulfillmentTypes.find(
        (oft) => oft.fulfillmentType === fulfillmentType
      )
      const isASAP = matchedStoreOFT?.orderType === 'ASAP'
      const earliestTimeslot = matchedStoreOFT?.earliestTimeslot
      if (earliestTimeslot && !isASAP) {
        const timeslotDatetime = parseISO(
          new Date(earliestTimeslot.from).toISOString()
        )
        const timeslotDate = format(timeslotDatetime, 'yyyy-MM-dd')
        const timeslotTime = format(timeslotDatetime, 'HH:mm')

        if (timeslotDate && sortCriteria.fulfillmentDate)
          setNavQueryDate(timeslotDate)
        queryParams.push(`date=${timeslotDate}`)
        // Always set time param from earliestTimeslot if orderType is not asap
        if (timeslotTime && matchedStoreOFT.orderType !== 'ASAP') {
          setNavQueryTime(timeslotTime)
          queryParams.push(`time=${encodeURIComponent(timeslotTime)}`)
        }
      }
    }

    if (!!queryParams.length) {
      let queryParamString
      queryParamString = queryParams.join('&')
      return `?${queryParamString}`
    }
    return ''
  }, [store.slug, store.orderFulfillmentTypes, fulfillmentType, sortCriteria])

  const queryPartnerOnNav = useCallback(() => {
    if (navQueryDate) {
      const dateTimeArgs = {
        date: navQueryDate,
        time: navQueryTime ? `${navQueryTime}:00` : undefined
      }
      // fetch partner data on page transition so it hydrates sooner
      fetchPartnerAndCustomer(dateTimeArgs, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navQueryDate, navQueryTime])

  return (
    <RouterAnchor
      hrefLink={storeHref}
      search={searchQueryParams}
      testId={`${fulfillmentType}-link`}
      onNavCallback={queryPartnerOnNav}
    >
      <FulfillmentLinkContainer inactive={!getLinkContent().isActive}>
        <FulfillmentLinkContent>
          <span>{getLinkContent().topContent}</span>
          <span>{getLinkContent().bottomContent}</span>
        </FulfillmentLinkContent>
      </FulfillmentLinkContainer>
    </RouterAnchor>
  )
}

const FulfillmentInfoContainer = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
  justifyContent: 'space-between'
}))

const FulfillmentLinkContainer = styled.div<
  StyledHTMLElement & { inactive: boolean }
>(({ theme, inactive }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: inactive ? theme.colors['primary'] : '#fff',
  backgroundColor: inactive ? '#fff' : theme.colors['primary'],
  padding: '8.5px 14px',
  width: '100%',
  borderRadius: theme.borderRadius,
  border: `1px solid ${theme.colors['primary']}`,
  cursor: 'pointer',
  textDecoration: 'none',
  flexGrow: 1,
  minHeight: '52px',

  ...disableBlueTapHighlight(),

  '& >svg': {
    minHeight: '20px',
    minWidth: '20px'
  },

  '&:hover': {
    opacity: 0.8
  },
  '&:active': {
    opacity: 0.6
  }
}))
const FulfillmentLinkContent = styled.span<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    '& span': {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: '15px'
    },
    '& span:first-of-type': {
      fontSize: '16px',
      textTransform: 'capitalize',
      fontWeight: 500,
      lineHeight: '18px'
    }
  })
)

export default FulfillmentInfo
